'use client';

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Loader2,
  CheckCircle,
  AlertCircle,
  Mail,
  Building2,
  User,
} from 'lucide-react';

interface ClaimProfileFormProps {
  listingId: string;
  companyName: string;
  isFromImport: boolean;
}

export function ClaimProfileForm({
  listingId,
  companyName,
  isFromImport,
}: ClaimProfileFormProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: companyName,
  });
  const [consentBenchmarking, setConsentBenchmarking] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState<'success' | 'error' | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitResult(null);
    setErrorMessage(null);

    try {
      const response = await fetch('/api/claim-profile', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          listing_id: listingId,
          is_from_import: isFromImport,
          claimant_name: formData.name,
          claimant_email: formData.email,
          claimant_company: formData.company,
          consent_benchmarking: consentBenchmarking,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to submit claim');
      }

      setSubmitResult('success');
    } catch (error) {
      console.error('Claim error:', error);
      setSubmitResult('error');
      setErrorMessage(error instanceof Error ? error.message : 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitResult === 'success') {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="h-8 w-8 text-green-600" />
        </div>
        <h3 className="text-xl font-semibold mb-2">Verification Email Sent!</h3>
        <p className="text-muted-foreground mb-4">
          We&apos;ve sent a verification email to <strong>{formData.email}</strong>.
        </p>
        <p className="text-sm text-muted-foreground">
          Please click the link in the email to verify your claim. The link will expire in 24 hours.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {submitResult === 'error' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3 text-red-800">
          <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium">Failed to submit claim</p>
            <p className="text-sm">{errorMessage || 'Please try again later.'}</p>
          </div>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="name">Your Full Name *</Label>
        <div className="relative">
          <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
            placeholder="John Smith"
            className="pl-10"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="email">Work Email *</Label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required
            placeholder="john@yourcompany.com"
            className="pl-10"
          />
        </div>
        <p className="text-xs text-muted-foreground">
          Please use a company email address for faster verification.
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="company">Company Name</Label>
        <div className="relative">
          <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            id="company"
            value={formData.company}
            onChange={(e) => setFormData({ ...formData, company: e.target.value })}
            className="pl-10"
            disabled
          />
        </div>
      </div>

      <div className="flex items-start gap-3 rounded-lg border p-4">
        <input
          id="consent-benchmarking"
          type="checkbox"
          checked={consentBenchmarking}
          onChange={(e) => setConsentBenchmarking(e.target.checked)}
          className="mt-1 h-4 w-4 shrink-0 accent-primary"
        />
        <div>
          <Label htmlFor="consent-benchmarking" className="cursor-pointer">
            Include {companyName} in benchmark evaluations
          </Label>
          <p className="text-xs text-muted-foreground mt-1">
            Rankings on Shipzy are earned, not bought: we periodically send
            test rate requests and score how fast you respond, how complete
            the quote is, and whether pricing is at market level. This is how
            your company qualifies for a Top&nbsp;10 spot. You can withdraw at
            any time by contacting us.
          </p>
        </div>
      </div>

      <div className="bg-slate-50 rounded-lg p-4 text-sm text-muted-foreground">
        <p className="font-medium text-foreground mb-2">What happens next?</p>
        <ol className="list-decimal list-inside space-y-1">
          <li>We&apos;ll send a verification email to confirm your identity</li>
          <li>Click the link in the email to verify your claim</li>
          <li>Complete your company profile with updated information</li>
          <li>Your profile will be reviewed and activated</li>
        </ol>
      </div>

      <Button type="submit" className="w-full" disabled={isSubmitting}>
        {isSubmitting ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Submitting...
          </>
        ) : (
          'Submit Claim Request'
        )}
      </Button>
    </form>
  );
}
